import '../../components/overlays/overlays.scss';

import React, { PropsWithChildren } from 'react';
import { observer } from 'mobx-react-lite';
import { usePopper } from 'react-popper';
import classNames from 'classnames';
import { Placement } from '@popperjs/core/lib';
import { Options as OffsetOptions } from '@popperjs/core/lib/modifiers/offset';
import { useDelayedUnmount } from '../../components/hooks';
import { Maybe } from '../../core';
import { OverlayState } from './overlayState';

type Props = PropsWithChildren<{
  model: OverlayState,
  className?: Maybe<string>,
  content?: string,
  placement?: Placement,
  offset?: OffsetOptions["offset"],
  usePadding?: boolean,
  id?: string
}>;

export const Overlay = observer(({
  model,
  placement = 'top',
  offset = [0, 8],
  usePadding = true,
  ...props
}: Props) => {
  const isVisible = model.isVisible || model.isPlayerTutorialOn;
  const { shouldRender, isAnimating } = useDelayedUnmount(isVisible, 400);
  const className = classNames('overlay menu-animate', props.className, isAnimating);

  const { styles, attributes } = usePopper(model.anchorRef.node, model.overlayRef.node, {
    placement: placement,
    strategy: 'fixed',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: model.arrowRef.node
        }
      },
      {
        name: 'offset',
        options: {
          offset: offset,
        }
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'top-end', 'top-start', 'left', 'right', 'bottom'],
        }
      }
    ],
  });

  return shouldRender ? (
    <div className={className}
      id={props.id}
      ref={model.overlayRef.ref}
      style={styles.popper} {...attributes.popper}>

      {usePadding && <div className="overlay-padding" />}

      <div className="overlay-arrow"
        ref={model.arrowRef.ref}
        style={styles.arrow} />

      <div className="overlay-content">
        {props.children}
      </div>
    </div>
  ) : null;
});